import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import {
  GridRowModes,
  GridToolbarContainer,
  GridActionsCellItem,
  useGridApiRef,
  GridBooleanCell,
} from '@mui/x-data-grid-premium';

import { dataGridPremiumColumns } from '../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  AutoCompleteSelectUI,
  DatagridPremiumUi,
  InputUI,
  CheckboxUI,
  DatePickerUI,
} from '../Interface';
import { formatCurrency } from '../../utils/currencyHelper';
import dayjs from 'dayjs';

const EditToolbar = (props) => {
  const { disabled, push, defProjectPaymentSchedule } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const newData = {
      ...defProjectPaymentSchedule,
      id: uuidv4(),
    };

    await push(newData);

    const fieldsToValidate = [
      `projectPaymentSchedule.${values.projectPaymentSchedule.length ?? 0}.name`,
      `projectPaymentSchedule.${values.projectPaymentSchedule.length ?? 0}.amount`,
      `projectPaymentSchedule.${values.projectPaymentSchedule.length ?? 0}.currencyCode`,
      `projectPaymentSchedule.${values.projectPaymentSchedule.length ?? 0}.invoiced`,
      `projectPaymentSchedule.${values.projectPaymentSchedule.length ?? 0}.invoiceNumber`,
      `projectPaymentSchedule.${values.projectPaymentSchedule.length ?? 0}.invoicedAmount`,
      `projectPaymentSchedule.${values.projectPaymentSchedule.length ?? 0}.paid`,
      `projectPaymentSchedule.${values.projectPaymentSchedule.length ?? 0}.paidAmount`,
      `projectPaymentSchedule.${values.projectPaymentSchedule.length ?? 0}.description`,
      `projectPaymentSchedule.${values.projectPaymentSchedule.length ?? 0}.dueDate`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const ProjectPaymentScheduleTable = ({ disabled, defProjectPaymentSchedule, autoCompleteOptions }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const getValueIndex = (values, id) => {
    return values.projectPaymentSchedule.findIndex((rc) => rc.id === id);
  };

  const columns = (remove) =>
    dataGridPremiumColumns([
      {
        field: 'name',
        headerName: 'Fizetési ütem',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`projectPaymentSchedule.${valueIndex}.name`}
              as={InputUI}
              variant="standard"
              fullWidth
              helperText={<FormErrorUI message={errors?.projectPaymentSchedule?.[valueIndex]?.name} />}
              onChange={(e) => {
                setFieldValue(`projectPaymentSchedule.${valueIndex}.name`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`projectPaymentSchedule.${valueIndex}.name`)}
            />
          );
        },
      },
      {
        field: 'amount',
        headerName: 'Összeg',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return values.projectPaymentSchedule?.[valueIndex]?.amount
            ? formatCurrency(values.projectPaymentSchedule?.[valueIndex]?.amount)
            : ' ';
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`projectPaymentSchedule.${valueIndex}.amount`}
              as={InputUI}
              variant="standard"
              type="number"
              fullWidth
              helperText={<FormErrorUI message={errors?.projectPaymentSchedule?.[valueIndex]?.amount} />}
              onChange={(e) => {
                setFieldValue(`projectPaymentSchedule.${valueIndex}.amount`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`projectPaymentSchedule.${valueIndex}.amount`)}
            />
          );
        },
      },
      {
        field: 'dueDate',
        headerName: 'Határidő',
        type: 'date',
        sortable: false,
        filterable: false,
        editable: true,
        valueGetter: (_data, row) => {
          return new Date(row.dueDate);
        },
        renderCell: (data) => {
          return dayjs(data.row.dueDate).format('YYYY.MM.DD');
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`projectPaymentSchedule.${valueIndex}.dueDate`}
              component={DatePickerUI}
              type="date"
              variant="standard"
              fullWidth
              helperText={<FormErrorUI message={errors?.projectPaymentSchedule?.[valueIndex]?.dueDate} />}
              value={values.projectPaymentSchedule?.[valueIndex]?.dueDate}
              onChange={(date) => {
                setFieldValue(`projectPaymentSchedule.${valueIndex}.dueDate`, date);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: new Date(date),
                });
              }}
              onBlur={async () => await validateField(`projectPaymentSchedule.${valueIndex}.dueDate`)}
            />
          );
        },
      },
      {
        field: 'currencyCode',
        headerName: 'Pénznem',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedEmployee = autoCompleteOptions.currency.find(
            (u) => u.value === values.projectPaymentSchedule?.[valueIndex]?.currencyCode
          );
          return findedEmployee?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedEmployee = autoCompleteOptions.currency.find(
            (u) => u.value === values.projectPaymentSchedule?.[valueIndex]?.currencyCode
          );
          return (
            <Field
              name={`projectPaymentSchedule.${valueIndex}.currencyCode`}
              variant="standard"
              as={AutoCompleteSelectUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.projectPaymentSchedule?.[valueIndex]?.currencyCode} />}
              onChange={(_e, newVal) => {
                const val = newVal?.value ?? null;

                setFieldValue(`projectPaymentSchedule.${valueIndex}.currencyCode`, val);

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: val,
                });
              }}
              onBlur={async () => await validateField(`projectPaymentSchedule.${valueIndex}.currencyCode`)}
              value={findedEmployee}
              isOptionEqualToValue={(option, value) => option.value === value}
              dataset={autoCompleteOptions.currency}
            />
          );
        },
      },
      {
        field: 'invoiced',
        type: 'string',
        headerName: 'Számlázva',
        editable: true,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return <GridBooleanCell {...data} value={values.projectPaymentSchedule?.[valueIndex]?.invoiced} />;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`projectPaymentSchedule.${valueIndex}.invoiced`}
              component={CheckboxUI}
              helperText={<FormErrorUI message={errors?.projectPaymentSchedule?.[valueIndex]?.invoiced} />}
              checked={values.projectPaymentSchedule?.[valueIndex]?.invoiced}
              variant="standard"
              onChange={(e) => {
                setFieldValue(`projectPaymentSchedule.${valueIndex}.invoiced`, e.target.checked ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.checked,
                });
              }}
              onBlur={async () => await validateField(`projectPaymentSchedule.${valueIndex}.invoiced`)}
            />
          );
        },
      },
      {
        field: 'invoiceNumber',
        headerName: 'Számlaszám',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`projectPaymentSchedule.${valueIndex}.invoiceNumber`}
              as={InputUI}
              variant="standard"
              fullWidth
              helperText={<FormErrorUI message={errors?.projectPaymentSchedule?.[valueIndex]?.invoiceNumber} />}
              onChange={(e) => {
                setFieldValue(`projectPaymentSchedule.${valueIndex}.invoiceNumber`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`projectPaymentSchedule.${valueIndex}.invoiceNumber`)}
            />
          );
        },
      },
      {
        field: 'invoicedAmount',
        headerName: 'Számlázott összeg',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return values.projectPaymentSchedule?.[valueIndex]?.invoicedAmount
            ? formatCurrency(values.projectPaymentSchedule?.[valueIndex]?.invoicedAmount)
            : ' ';
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`projectPaymentSchedule.${valueIndex}.invoicedAmount`}
              as={InputUI}
              variant="standard"
              type="number"
              fullWidth
              helperText={<FormErrorUI message={errors?.projectPaymentSchedule?.[valueIndex]?.invoicedAmount} />}
              onChange={(e) => {
                setFieldValue(`projectPaymentSchedule.${valueIndex}.invoicedAmount`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`projectPaymentSchedule.${valueIndex}.invoicedAmount`)}
            />
          );
        },
      },
      {
        field: 'paid',
        type: 'string',
        headerName: 'Fizetve',
        editable: true,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return <GridBooleanCell {...data} value={values.projectPaymentSchedule?.[valueIndex]?.paid} />;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`projectPaymentSchedule.${valueIndex}.paid`}
              component={CheckboxUI}
              helperText={<FormErrorUI message={errors?.projectPaymentSchedule?.[valueIndex]?.paid} />}
              checked={values.projectPaymentSchedule?.[valueIndex]?.paid}
              variant="standard"
              onChange={(e) => {
                setFieldValue(`projectPaymentSchedule.${valueIndex}.paid`, e.target.checked ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.checked,
                });
              }}
              onBlur={async () => await validateField(`projectPaymentSchedule.${valueIndex}.paid`)}
            />
          );
        },
      },
      {
        field: 'paidAmount',
        headerName: 'Fizetve összeg',
        type: 'string',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return values.projectPaymentSchedule?.[valueIndex]?.paidAmount
            ? formatCurrency(values.projectPaymentSchedule?.[valueIndex]?.paidAmount)
            : ' ';
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`projectPaymentSchedule.${valueIndex}.paidAmount`}
              as={InputUI}
              variant="standard"
              type="number"
              fullWidth
              helperText={<FormErrorUI message={errors?.projectPaymentSchedule?.[valueIndex]?.paidAmount} />}
              onChange={(e) => {
                setFieldValue(`projectPaymentSchedule.${valueIndex}.paidAmount`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`projectPaymentSchedule.${valueIndex}.paidAmount`)}
            />
          );
        },
      },
      {
        field: 'description',
        headerName: 'Megjegyzés',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`projectPaymentSchedule.${valueIndex}.description`}
              as={InputUI}
              variant="standard"
              fullWidth
              helperText={<FormErrorUI message={errors?.projectPaymentSchedule?.[valueIndex]?.description} />}
              onChange={(e) => {
                setFieldValue(`projectPaymentSchedule.${valueIndex}.description`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`projectPaymentSchedule.${valueIndex}.description`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="projectPaymentSchedule">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.ProjectPaymentScheduleTable}
                height={480}
                columns={columns(remove)}
                rows={values?.projectPaymentSchedule ?? []}
                editMode="row"
                slots={{
                  toolbar: EditToolbar,
                }}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defProjectPaymentSchedule,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                headerFilters={false}
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;

                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default ProjectPaymentScheduleTable;
